import i18n from "@/i18n";
/**
 * VuosilomaOpeningDialog — VUOSILOMA-V1. Records opening facts:
 * opening date (first of a month), balances for earlier holiday years
 * (one per year), and full months already earned this holiday year.
 * Blank = not recorded (different from an explicit 0). Reason required.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import {
    buildOpeningPayload, creditYearLabel, isFirstOfMonth, priorYearOptions,
    vlErrorMessage,
} from "./vuosilomaHelpers";

function PriorRow({ row, idx, options, onChange, onRemove }) {
    return (
        <div className="flex items-center gap-2" data-testid={`vl-prior-row-${idx}`}>
            <Select value={row.year} onValueChange={(v) => onChange({ ...row, year: v })}>
                <SelectTrigger className="w-44" data-testid={`vl-prior-year-${idx}`}>
                    <SelectValue placeholder={i18n.t("wf.vuosilomaOpeningDialog.holidayYear", "Holiday year")} />
                </SelectTrigger>
                <SelectContent>
                    {options.map((o) => (
                        <SelectItem key={o} value={o} data-testid={`vl-prior-year-${idx}-opt-${o}`}>
                            {creditYearLabel(o)}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <Input type="number" min="0" step="0.5" className="w-24"
                placeholder={i18n.t("wf.vuosilomaOpeningDialog.days", "Days")} value={row.days}
                data-testid={`vl-prior-days-${idx}`}
                onChange={(e) => onChange({ ...row, days: e.target.value })} />
            <Button variant="ghost" size="sm" onClick={onRemove}
                data-testid={`vl-prior-remove-${idx}`}>{i18n.t("common.remove", "Remove")}</Button>
        </div>
    );
}

export default function VuosilomaOpeningDialog({
    open, onOpenChange, employeeId, boundary, onSaved,
}) {
    const [date, setDate] = useState("");
    const [months, setMonths] = useState("");
    const [sick, setSick] = useState("");
    const [sickCal, setSickCal] = useState("");
    const [epStart, setEpStart] = useState("");
    const [epDays, setEpDays] = useState("");
    const [rows, setRows] = useState([]);
    const [reason, setReason] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setDate(""); setMonths(""); setSick(""); setRows([]); setReason(""); setError("");
            setSickCal(""); setEpStart(""); setEpDays("");
        }
    }, [open]);

    const effDate = boundary || date;
    const options = priorYearOptions(effDate);
    const dateOk = !!boundary || isFirstOfMonth(date);
    const canSave = !pending && dateOk && reason.trim().length >= 3;

    const save = async () => {
        setPending(true);
        setError("");
        try {
            await api.post(`/workforce/employees/${employeeId}/vuosiloma/opening`,
                buildOpeningPayload({ effectiveDate: date, reason,
                    currentMonths: months, priorRows: rows, sickDays: sick,
                    sickCalendarDays: sickCal, episodeStart: epStart, episodeDays: epDays,
                    boundaryRecorded: !!boundary }));
            toast.success(i18n.t("wf.vuosilomaOpeningDialog.openingFactsSaved", "Opening facts saved"));
            onOpenChange(false);
            onSaved?.();
        } catch (err) {
            setError(vlErrorMessage(err));
        } finally {
            setPending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="vl-opening-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{boundary ? i18n.t("wf.vuosilomaOpeningDialog.addOpeningFacts", "Add opening facts") : i18n.t("wf.vuosilomaOpeningDialog.recordAnnualLeaveOpening", "Record annual-leave opening")}</DialogTitle>
                    <DialogDescription>
                        {i18n.t("wf.vuosilomaOpeningDialog.leaveAFieldBlankIfIt", "Leave a field blank if it is not known. Blank is not the same as 0. Employment start date stays as recorded in Employment.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                    {!boundary && (
                        <label className="block">
                            <span className="text-neutral-700">Opening date (first day of a month)</span>
                            <Input type="date" className="mt-1" value={date}
                                data-testid="vl-opening-date"
                                onChange={(e) => setDate(e.target.value)} />
                            <span className="mt-1 block text-xs text-neutral-500">
                                {i18n.t("wf.vuosilomaOpeningDialog.miseoneCalculatesFromThisDateEarlier", "MiseOne calculates from this date. Earlier months come from the facts below.")}
                            </span>
                        </label>
                    )}
                    <label className="block">
                        <span className="text-neutral-700">{i18n.t("wf.vuosilomaOpeningDialog.fullMonthsAlreadyEarnedThisHoliday", "Full months already earned this holiday year")}</span>
                        <Input type="number" min="0" max="12" className="mt-1 w-28"
                            value={months} data-testid="vl-opening-current-months"
                            onChange={(e) => setMonths(e.target.value)} />
                    </label>
                    <label className="block">
                        <span className="text-neutral-700">{i18n.t("wf.vuosilomaOpeningDialog.sickLeaveWorkdaysAlreadyCountedThis", "Sick-leave workdays already counted this holiday year")}</span>
                        <Input type="number" min="0" className="mt-1 w-28"
                            value={sick} data-testid="vl-opening-sick-days"
                            onChange={(e) => setSick(e.target.value)} />
                        <span className="mt-1 block text-xs text-neutral-500">
                            Needed to apply the 75-workday limit. Blank = not known.
                        </span>
                    </label>
                    <label className="block">
                        <span className="text-neutral-700">{i18n.t("wf.vuosilomaOpeningDialog.sickLeaveCalendarDaysAlreadyCounted", "Sick-leave calendar days already counted this holiday year")}</span>
                        <Input type="number" min="0" className="mt-1 w-28"
                            value={sickCal} data-testid="vl-opening-sick-calendar-days"
                            onChange={(e) => setSickCal(e.target.value)} />
                        <span className="mt-1 block text-xs text-neutral-500">
                            35-hour rule: 105 calendar days per holiday year. Blank = not known.
                        </span>
                    </label>
                    <div className="space-y-1">
                        <span className="text-neutral-700">{i18n.t("wf.vuosilomaOpeningDialog.sickLeaveStillOpenOnThe", "Sick leave still open on the opening date")}</span>
                        <div className="flex gap-2">
                            <Input type="date" className="w-44" value={epStart}
                                data-testid="vl-opening-episode-start"
                                onChange={(e) => setEpStart(e.target.value)} />
                            <Input type="number" min="0" max="75" className="w-28" placeholder={i18n.t("wf.vuosilomaOpeningDialog.workdays", "Workdays")}
                                value={epDays} data-testid="vl-opening-episode-days"
                                onChange={(e) => setEpDays(e.target.value)} />
                        </div>
                        <span className="block text-xs text-neutral-500">
                            {i18n.t("wf.vuosilomaOpeningDialog.startDateAndWorkdaysAlreadyCounted", "Start date and workdays already counted for it. Needed only if it continues past 31 March.")}
                        </span>
                    </div>
                    <div className="space-y-2">
                        <span className="text-neutral-700">{i18n.t("wf.vuosilomaOpeningDialog.remainingBalanceFromEarlierHolidayYears", "Remaining balance from earlier holiday years")}</span>
                        {rows.map((r, i) => (
                            <PriorRow key={i} row={r} idx={i} options={options}
                                onChange={(v) => setRows(rows.map((x, j) => (j === i ? v : x)))}
                                onRemove={() => setRows(rows.filter((_, j) => j !== i))} />
                        ))}
                        <Button variant="outline" size="sm" disabled={!effDate}
                            data-testid="vl-prior-add"
                            onClick={() => setRows([...rows, { year: "", days: "" }])}>
                            {i18n.t("wf.vuosilomaOpeningDialog.addEarlierYear", "Add earlier year")}
                        </Button>
                    </div>
                    <label className="block">
                        <span className="text-neutral-700">{i18n.t("wf.vuosilomaOpeningDialog.reason", "Reason")}</span>
                        <Textarea rows={2} className="mt-1" value={reason}
                            data-testid="vl-opening-reason"
                            placeholder={i18n.t("wf.vuosilomaOpeningDialog.eGBalancesFromPreviousPayroll", "e.g. Balances from previous payroll system")}
                            onChange={(e) => setReason(e.target.value)} />
                    </label>
                    {error && (
                        <div role="alert" data-testid="vl-opening-error"
                            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                            {error}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}
                        data-testid="vl-opening-cancel">{i18n.t("common.cancel", "Cancel")}</Button>
                    <Button onClick={save} disabled={!canSave} data-testid="vl-opening-save">
                        {pending ? i18n.t("common.saving", "Saving…") : i18n.t("common.save", "Save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
